/**
 * Aspect Admin — Overview dashboard.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, meter, toast, openAssistant } from "./shell.js";
import { sparkline, trendChart, donutChart, legend } from "./charts.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { KPIS, TREND, USAGE_DONUT, INSIGHTS, USAGE_CAPS, WORKSPACE } from "../mock_data.js";

function kpiCard(k) {
  const deltaCls = k.dir === "up" ? "ap-delta--up" : k.good ? "ap-delta--up" : "ap-delta--down";
  const deltaIcon = k.dir === "up" ? "arrowUpRight" : "arrowDownRight";
  return `
    <div class="ap-panel ap-panel--hover ap-kpi">
      <div class="ap-kpi__top">
        <span class="ap-kpi__label">${t(k.label)}</span>
        <span class="ap-kpi__icon">${icon(k.icon, 16)}</span>
      </div>
      <div class="ap-kpi__value">${k.value}</div>
      <div class="ap-kpi__meta">
        <span class="ap-delta ${deltaCls}">${icon(deltaIcon)}${Math.abs(k.delta)}%</span>
        <span>${t(k.note)}</span>
      </div>
      <div class="ap-kpi__spark">${sparkline(k.spark)}</div>
    </div>
  `;
}

function insightCard(ins) {
  return `
    <div class="ap-insight">
      <div class="ap-insight__head">
        ${icon("sparkles", 15)}
        <span class="ap-insight__tag">${t(ins.tag)}</span>
      </div>
      <h4 class="ap-insight__title">${t(ins.title)}</h4>
      <p class="ap-insight__body">${t(ins.body)}</p>
      <div class="ap-insight__foot">
        <span class="ap-conf">${t("Confidence")}
          <span class="ap-conf__bars">${[1, 2, 3].map((i) => `<i class="${i <= ins.conf ? "on" : ""}"></i>`).join("")}</span>
        </span>
        <button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-nav="${ins.route}">${t(ins.action)}</button>
      </div>
    </div>
  `;
}

const QUICK_ACTIONS = [
  { icon: "plus", title: "New space", sub: "Start from a plan upload or blank shell", route: "/spaces" },
  { icon: "wand", title: "Run AI staging", sub: "Furnish a draft with one prompt", route: "/jobs" },
  { icon: "users", title: "Invite teammate", sub: "Editors, viewers, or admins", route: "/team" },
  { icon: "external", title: "Share a tour", sub: "Copy a client-ready link", route: "/spaces" },
];

export function renderOverview(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const firstName = (session.name || "there").split(/\s+/)[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? t("Good morning") : hour < 18 ? t("Good afternoon") : t("Good evening");
  const trend = { ...TREND, labels: TREND.labels.map((l) => t(l)), series: TREND.series.map((s) => ({ ...s, name: t(s.name) })) };

  const content = `
    <div class="ap-overview">
      ${pageHead(`${greet}, ${firstName}`, t("Here's what's moving across <strong>{ws}</strong> today.", { ws: WORKSPACE.name }), `
        <button type="button" class="ap-btn" data-nav="/analytics">${icon("calendar", 15)} ${t("Last 30 days")}</button>
        <button type="button" class="ap-btn ap-btn--primary" data-nav="/spaces">${icon("plus", 15)} ${t("New space")}</button>
      `)}

      <div class="ap-aistrip ap-overview__aistrip">
        <div class="ap-aistrip__orb">${icon("sparkles", 16)}</div>
        <div class="ap-aistrip__body">
          <h3>${t("AI summary")}</h3>
          <p>${t("Momentum is strong: tour sessions are up <strong>8.1%</strong> and AI generations up <strong>23.6%</strong> month over month. The render queue absorbed this morning's Meridian batch without breaching latency targets. <strong>One item needs attention</strong> — the Warehouse Loft plan analysis came back below the confidence threshold.")}</p>
        </div>
        <button type="button" class="ap-btn ap-btn--ai ap-btn--sm" id="ov-ask">${icon("sparkles", 14)} ${t("Ask a question")}</button>
      </div>

      <div class="ap-grid ap-grid--kpi ap-overview__kpis">
        ${KPIS.map(kpiCard).join("")}
      </div>

      <div class="ap-overview__body">
        <div class="ap-overview__primary">
          <div class="ap-panel ap-overview__chart">
            <div class="ap-panel__head">
              <div>
                <h3 class="ap-panel__title">${t("Engagement trend")}</h3>
                <p class="ap-panel__sub">${t("Tour sessions vs AI generations · weekly")}</p>
              </div>
              <div class="ap-seg">
                <button type="button" class="ap-seg__btn" data-range="7d">${t("7d")}</button>
                <button type="button" class="ap-seg__btn ap-seg__btn--on" data-range="30d">${t("30d")}</button>
                <button type="button" class="ap-seg__btn" data-range="90d">${t("90d")}</button>
              </div>
            </div>
            <div class="ap-panel__body">
              ${trendChart(trend, { w: 960, h: 150 })}
              <div class="ap-overview__legend">${legend(trend.series)}</div>
            </div>
          </div>

          <div class="ap-panel ap-overview__insights">
            <div class="ap-panel__head">
              <h3 class="ap-panel__title">${icon("sparkles", 16)} ${t("AI insights")}</h3>
              <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" id="ov-refresh">${icon("refresh", 14)} ${t("Refresh")}</button>
            </div>
            <div class="ap-panel__body">
              <div class="ap-grid ap-grid--3 ap-overview__insight-grid">
                ${INSIGHTS.map(insightCard).join("")}
              </div>
            </div>
          </div>
        </div>

        <div class="ap-overview__side">
          <div class="ap-panel ap-panel--pad ap-overview__plan">
            <div class="ap-panel__head">
              <h3 class="ap-panel__title">${t("Plan usage")}</h3>
              <a class="ap-btn ap-btn--ghost ap-btn--sm" href="#/billing">${t("Manage")}</a>
            </div>
            <div class="ap-overview__meters">
              ${USAGE_CAPS.slice(0, 3).map((u) => meter(u.label, u.used, u.cap, u.unit)).join("")}
            </div>
          </div>

          <div class="ap-panel ap-panel--pad ap-overview__mix">
            <div class="ap-panel__head">
              <h3 class="ap-panel__title">${t("Usage mix")}</h3>
            </div>
            <div class="ap-overview__donut">
              ${donutChart(USAGE_DONUT, { size: 168, stroke: 18, centerLabel: "1,904", centerSub: t("jobs / 30d") })}
              <div class="ap-overview__mix-legend">
                ${USAGE_DONUT.map(
                  (s) => `
                  <div class="ap-overview__mix-row">
                    <span><span class="ap-legend__swatch" style="background:${s.color}"></span>${t(s.label)}</span>
                    <strong>${s.value}%</strong>
                  </div>`
                ).join("")}
              </div>
            </div>
          </div>
        </div>

        <div class="ap-overview__bottom">
          <div class="ap-panel ap-overview__quick">
            <div class="ap-panel__head">
              <h3 class="ap-panel__title">${t("Quick actions")}</h3>
            </div>
            <div class="ap-panel__body">
              <div class="ap-grid ap-overview__quick-grid">
                ${QUICK_ACTIONS.map(
                  (q) => `
                  <button type="button" class="ap-panel ap-panel--hover ap-quick" data-nav="${q.route}">
                    <span class="ap-kpi__icon">${icon(q.icon, 16)}</span>
                    <strong>${t(q.title)}</strong>
                    <span>${t(q.sub)}</span>
                  </button>`
                ).join("")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  const content_el = renderShell(root, "/dashboard", content);
  if (!content_el) return;

  content_el.addEventListener("click", (e) => {
    const nav = e.target.closest("[data-nav]");
    if (nav) navigate(nav.dataset.nav);
  });

  content_el.querySelector("#ov-ask")?.addEventListener("click", (e) => {
    e.stopPropagation();
    openAssistant(root);
  });

  content_el.querySelector("#ov-refresh")?.addEventListener("click", () => {
    toast(root, t("Insights refreshed from the last 30 days of data"), "ok");
  });

  content_el.querySelectorAll("[data-range]").forEach((b) =>
    b.addEventListener("click", () => {
      content_el.querySelectorAll("[data-range]").forEach((x) => x.classList.remove("ap-seg__btn--on"));
      b.classList.add("ap-seg__btn--on");
      toast(root, t("Range set to {r}", { r: b.dataset.range }), "info");
    })
  );
}
